/**
 * useCustomTypes Hook
 *
 * High-performance, memoized hook for components that only need custom types data.
 * Reads pre-rendered data from dataset attributes (populated by footer.jsp).
 *
 * Use cases:
 * - Components that need enum/type definitions
 * - Icon components
 * - Form field components
 *
 * Benefits:
 * - Zero API calls - uses server-side pre-rendered data
 * - Instant availability on page load
 * - Stable references prevent unnecessary re-renders
 * - Graceful fallbacks
 */

import { useMemo } from 'react';
import type {
  CustomTypesResponse,
  ChatbotPositionEnum,
  ChatbotIconEnum,
} from '../types/customTypes';

export interface UseCustomTypesReturn {
  /** Custom types data - null if not loaded */
  customTypes: CustomTypesResponse | null;

  /** Whether custom types are loaded and available */
  isLoaded: boolean;

  /** Custom types error if any */
  error: unknown;

  /** Chatbot position enum values */
  positionEnum: ChatbotPositionEnum | null;

  /** Chatbot icon enum values */
  iconEnum: ChatbotIconEnum | null;
}

/**
 * Hook for accessing custom types with optimizations for rendering components.
 * Data is read from pre-rendered HTML dataset attributes.
 *
 * @example
 * ```tsx
 * const { customTypes, positionEnum, iconEnum, isLoaded } = useCustomTypes();
 *
 * if (!isLoaded) return <LoadingSpinner />;
 *
 * return <IconSelector options={iconEnum} />;
 * ```
 */
export const useCustomTypes = (): UseCustomTypesReturn => {
  return useMemo(() => {
    try {
      // Read pre-rendered data from dataset attribute
      const rootElement = document.getElementById('react-app-root');
      const datasetValue = rootElement?.dataset?.chatbotCustomTypes;

      if (!datasetValue) {
        return {
          customTypes: null,
          isLoaded: false,
          error: null,
          positionEnum: null,
          iconEnum: null,
        };
      }

      // Parse the JSON data
      const customTypes = JSON.parse(datasetValue) as CustomTypesResponse;
      const enums = customTypes?.payload?.enums;

      return {
        customTypes,
        isLoaded: true,
        error: null,
        positionEnum: enums?.ChatbotPositionEnum || null,
        iconEnum: enums?.ChatbotIconEnum || null,
      };
    } catch (error) {
      console.error('Failed to parse custom types from dataset:', error);
      return {
        customTypes: null,
        isLoaded: false,
        error,
        positionEnum: null,
        iconEnum: null,
      };
    }
  }, []); // Empty deps - data is static from server-side rendering
};
